import React, {
  useState,
  ChangeEvent,
} from 'react';
import classnames from 'classnames';
import { FieldProps } from 'formik';
import Tooltip from '../../tooltip/Tooltip';

import styles from './TextField.module.scss';

export type TextFieldProps<T> = FieldProps<T> & BasicFieldProps & {
  field: {
    value: string | undefined;
  };
  tooltip?: string;
  helper?: string;
  maxLength?: number;
  maxLengthDisplay?: (currentLength: number, maxLength: number) => string;
};

function TextField<T extends string | number | undefined>({
  field: {
    value,
    name,
    checked,
    onBlur,
  },
  form,
  type = 'text',
  className,
  label,
  disabled = false,
  tooltip,
  helper,
  maxLength,
  maxLengthDisplay,
}: TextFieldProps<T>): JSX.Element {
  const [fieldFocus, setFieldFocus] = useState(false);
  function handleFocus() {
    setFieldFocus(true);
  }

  function handleBlur(e: ChangeEvent<HTMLInputElement>) {
    setFieldFocus(false);
    onBlur(e);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    if (maxLength) {
      form.setFieldValue(name, e.target.value.slice(0, maxLength));
    } else if (type === 'number') {
      form.setFieldValue(name, +e.target.value);
    } else {
      form.setFieldValue(name, e.target.value);
    }
  }

  const valid = (form?.touched?.[name] || false) && !(form?.errors?.[name] || false);
  const invalid = (form?.touched?.[name] || false) && (form?.errors?.[name] || false);

  return (
    <div className={classnames(
      styles.textFieldContainer,
      className,
    )}
    >
      <div
        data-testid={`text-field-${name}`}
        className={classnames(styles.textField, {
          [styles.textFieldFocus]: fieldFocus,
          [styles.textFieldDisabled]: disabled,
          [styles.textFieldWithTooltip]: !!tooltip,
          [styles.textFieldValid]: valid,
          [styles.textFieldInvalid]: invalid,
        })}
      >

        <input
          id={name}
          type={type}
          value={value}
          name={name}
          className={styles.textFieldInput}
          checked={checked}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled}
        />
        {label && (
          <label htmlFor={name}>
            {label}
          </label>
        )}
        {tooltip && (
          <Tooltip
            text={tooltip}
            disabled={disabled}
          />
        )}
      </div>
      <div className={styles.foot}>
        {helper && !invalid && (
          <span
            data-testid={`helper-text-field-${name}`}
            className={styles.helper}
          >
            {helper}
          </span>
        )}
        { maxLength && (
          <span
            className={classnames(styles.counter, {
              [styles.counterMaxLengthReached]: maxLength === value?.length,
            })}
            dangerouslySetInnerHTML={{
              __html: maxLengthDisplay
                ? maxLengthDisplay(value?.length || 0, maxLength)
                : `${value?.length || 0}/maxLength`,
            }}
          />
        )}
      </div>
    </div>
  );
}

export default TextField;
